import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const PAGE_SIZE = 50;

const toItems = (data) => {
  if (Array.isArray(data?.items)) return data.items;
  return (data?.options || []).map((o) => (typeof o === 'string' ? { value: o, label: o, count: null } : o));
};

// Shared typeahead hook for every `*-options` endpoint (plan 2092 A2).
// Debounced (250 ms), aborts the in-flight request when a newer one starts, keeps
// the previous options on screen while the next page loads, and can page with
// loadMore() (`paged: true`, shared-contract endpoints only). `extraParams` (e.g. a date range) is watched by value, not identity.
// Endpoints on the shared contract return {options, items, total, has_more};
// legacy ones returning only {options: [...]} still work (hasMore stays false).
export function useSuggestionsPage(url, query, extraParams = {}, { enabled = true, minChars = 0, limit = PAGE_SIZE, paged = false, select = null } = {}) {
  const [state, setState] = useState({ items: [], total: 0, hasMore: false, loading: false, error: null });
  const controllerRef = useRef(null);
  const paramsKey = JSON.stringify(extraParams);
  const itemsRef = useRef([]);
  const queryRef = useRef(query);
  queryRef.current = query;
  // `select(data)` maps a non-standard response (`{candidates}`, `{people}`, grouped /search)
  // to the item list; kept in a ref so an inline arrow doesn't re-fire the fetch.
  const selectRef = useRef(select);
  selectRef.current = select;

  const fetchPage = useCallback(async (offset) => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const response = await axios.get(url, {
        // limit/offset only go to endpoints on the shared contract; legacy ones cap `limit` themselves.
        params: { q: queryRef.current, ...(paged ? { limit, offset } : {}), ...JSON.parse(paramsKey) },
        signal: controller.signal,
        withCredentials: true, // the pickers this replaced used fetch(credentials: 'include')
      });
      const page = selectRef.current ? selectRef.current(response.data) : toItems(response.data);
      const items = offset === 0 ? page : [...itemsRef.current, ...page];
      itemsRef.current = items;
      setState({
        items,
        total: typeof response.data?.total === 'number' ? response.data.total : items.length,
        hasMore: !!response.data?.has_more,
        loading: false,
        error: null,
      });
    } catch (error) {
      if (axios.isCancel(error) || controller.signal.aborted) return;
      console.error(`Error fetching suggestions from ${url}:`, error);
      // a failed fresh search must not leave the previous query's results on screen
      if (offset === 0) itemsRef.current = [];
      setState((s) => ({ ...s, ...(offset === 0 ? { items: [], total: 0, hasMore: false } : {}), loading: false, error }));
    }
  }, [url, paramsKey, limit, paged]);

  useEffect(() => {
    if (!enabled || !url) return undefined;
    if ((query || '').trim().length < minChars) {
      controllerRef.current?.abort();
      itemsRef.current = [];
      setState({ items: [], total: 0, hasMore: false, loading: false, error: null });
      return undefined;
    }
    // spinner from the first keystroke, not only once the debounce fires (avoids a "No options" flash)
    setState((s) => ({ ...s, loading: true }));
    const timer = setTimeout(() => fetchPage(0), 250);
    return () => clearTimeout(timer);
  }, [url, query, paramsKey, enabled, minChars, fetchPage]);

  useEffect(() => () => controllerRef.current?.abort(), []);

  const loadMore = useCallback(() => {
    if (state.hasMore && !state.loading) fetchPage(itemsRef.current.length);
  }, [state.hasMore, state.loading, fetchPage]);

  return { ...state, options: state.items.map((i) => i?.value), loadMore };
}

// Back-compat wrapper: the plain list of option strings, as every existing caller expects.
export default function useSuggestions(url, query, extraParams = {}, enabled = true) {
  return useSuggestionsPage(url, query, extraParams, { enabled }).options;
}
